import { useMemo, useState } from 'react';
import { Team, Venue, GroupMatch, KnockoutMatch } from '../types/tournament';
import { getTeamById, getVenueById, formatMatchDate, formatMatchTime } from '../hooks/useBracketData';

interface MatchListProps {
  groupMatches: GroupMatch[];
  knockoutMatches: KnockoutMatch[];
  teams: Team[];
  venues: Venue[];
}

type FilterStage = 'all' | 'group' | 'R32' | 'R16' | 'QF' | 'SF' | 'finals';

function getRoundLabel(round: string): string {
  const labels: Record<string, string> = {
    R32: 'Round of 32',
    R16: 'Round of 16',
    QF: 'Quarter-final',
    SF: 'Semi-final',
    '3RD': '3rd Place',
    F: 'Final',
  };
  return labels[round] || round;
}

function getShortRoundLabel(round: string): string {
  const labels: Record<string, string> = {
    R32: 'R32',
    R16: 'R16',
    QF: 'QF',
    SF: 'SF',
    '3RD': '3rd',
    F: 'Final',
  };
  return labels[round] || round;
}

function formatTeamSource(source: string): string {
  if (source.match(/^[123][A-L]+$/)) {
    const position = source[0];
    const groups = source.slice(1);
    if (position === '1') return `Winner ${groups}`;
    if (position === '2') return `Runner-up ${groups}`;
    if (position === '3') {
      if (groups.length > 1) return `3rd (${groups.split('').join('/')})`;
      return `3rd ${groups}`;
    }
  }
  if (source.startsWith('W')) return `Winner M${source.slice(1)}`;
  if (source.startsWith('L')) return `Loser M${source.slice(1)}`;
  return source;
}

export function MatchList({ groupMatches, knockoutMatches, teams, venues }: MatchListProps) {
  const [filter, setFilter] = useState<FilterStage>('all');

  const allMatches = useMemo(() => {
    const group = groupMatches.map(m => ({
      ...m,
      stage: 'Group' as const,
      team1: m.team1Id ? getTeamById(teams, m.team1Id) : undefined,
      team2: m.team2Id ? getTeamById(teams, m.team2Id) : undefined,
      venue: getVenueById(venues, m.venueId),
    }));

    const knockout = knockoutMatches.map(m => ({
      ...m,
      stage: m.round as string,
      team1: undefined as Team | undefined,
      team2: undefined as Team | undefined,
      venue: getVenueById(venues, m.venueId),
      team1Source: m.team1Source,
      team2Source: m.team2Source,
    }));

    return [...group, ...knockout];
  }, [groupMatches, knockoutMatches, teams, venues]);

  const filteredMatches = useMemo(() => {
    if (filter === 'all') return allMatches;
    if (filter === 'group') return allMatches.filter(m => m.stage === 'Group');
    if (filter === 'finals') return allMatches.filter(m => m.stage === '3RD' || m.stage === 'F');
    return allMatches.filter(m => m.stage === filter);
  }, [allMatches, filter]);

  // Group matches by date
  const matchesByDate = useMemo(() => {
    const grouped = new Map<string, typeof filteredMatches>();
    filteredMatches.forEach(match => {
      const date = match.date;
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(match);
    });
    return grouped;
  }, [filteredMatches]);

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">FIFA World Cup 2026 - All 104 Matches</h1>

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
          All (104)
        </FilterButton>
        <FilterButton active={filter === 'group'} onClick={() => setFilter('group')}>
          Group Stage (72)
        </FilterButton>
        <FilterButton active={filter === 'R32'} onClick={() => setFilter('R32')}>
          Round of 32 (16)
        </FilterButton>
        <FilterButton active={filter === 'R16'} onClick={() => setFilter('R16')}>
          Round of 16 (8)
        </FilterButton>
        <FilterButton active={filter === 'QF'} onClick={() => setFilter('QF')}>
          Quarter-finals (4)
        </FilterButton>
        <FilterButton active={filter === 'SF'} onClick={() => setFilter('SF')}>
          Semi-finals (2)
        </FilterButton>
        <FilterButton active={filter === 'finals'} onClick={() => setFilter('finals')}>
          3rd Place & Final (2)
        </FilterButton>
      </div>

      {/* Match table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-fifa-blue text-white">
            <tr>
              <th className="px-3 py-2 text-left text-sm">#</th>
              <th className="px-3 py-2 text-left text-sm">Date</th>
              <th className="px-3 py-2 text-left text-sm hidden sm:table-cell">Time (ET)</th>
              <th className="px-3 py-2 text-left text-sm">Stage</th>
              <th className="px-3 py-2 text-left text-sm">Team 1</th>
              <th className="px-3 py-2 text-center text-sm">vs</th>
              <th className="px-3 py-2 text-left text-sm">Team 2</th>
              <th className="px-3 py-2 text-left text-sm">Venue</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(matchesByDate.entries()).map(([date, matches]) => (
              <>
                <tr key={`date-${date}`} className="bg-gray-100">
                  <td colSpan={8} className="px-3 py-2 font-semibold text-gray-700">
                    {formatFullDate(date)}
                  </td>
                </tr>
                {matches.map((match) => (
                  <tr key={match.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm font-medium">M{match.matchNumber}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{formatMatchDate(match.date)}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 hidden sm:table-cell">{formatMatchTime(match.time)}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStageColor(match.stage)}`}>
                        <span className="hidden sm:inline">
                          {match.stage === 'Group' ? `Group ${(match as GroupMatch).group}` : getRoundLabel(match.stage)}
                        </span>
                        <span className="sm:hidden">
                          {match.stage === 'Group' ? `G-${(match as GroupMatch).group}` : getShortRoundLabel(match.stage)}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {match.team1 ? (
                        <span className="flex items-center gap-2">
                          <span>{match.team1.flag}</span>
                          <span className="hidden sm:inline">{match.team1.name}</span>
                          <span className="sm:hidden">{match.team1.code}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          {'team1Source' in match ? formatTeamSource(match.team1Source) : 'TBD'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-400">vs</td>
                    <td className="px-3 py-2 text-sm">
                      {match.team2 ? (
                        <span className="flex items-center gap-2">
                          <span>{match.team2.flag}</span>
                          <span className="hidden sm:inline">{match.team2.name}</span>
                          <span className="sm:hidden">{match.team2.code}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          {'team2Source' in match ? formatTeamSource(match.team2Source) : 'TBD'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {match.venue ? (
                        <span title={match.venue.name}>{match.venue.city}</span>
                      ) : (
                        'TBD'
                      )}
                    </td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        active
          ? 'bg-fifa-blue text-white'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function getStageColor(stage: string): string {
  switch (stage) {
    case 'Group':
      return 'bg-blue-100 text-blue-800';
    case 'R32':
      return 'bg-purple-100 text-purple-800';
    case 'R16':
      return 'bg-indigo-100 text-indigo-800';
    case 'QF':
      return 'bg-orange-100 text-orange-800';
    case 'SF':
      return 'bg-red-100 text-red-800';
    case '3RD':
      return 'bg-amber-100 text-amber-800';
    case 'F':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
